import { IconButton } from '@creditornot/cb-button';
import { Cross, MoreHorizontal } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuItemGroup, MenuLinkItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import GirlShrugging from 'common/illustrations/GirlShrugging';
import { Typography as T } from 'common/typography';

import ItemList from './ItemList';
import { ItemData, ListElement } from '../types';

type Props = {
  items: ListElement[];
  isLoadingItems: boolean;
  menuId: string | null;
  draggedItemIds: string[] | undefined;
  header: ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  onMoveItems: (itemsData: ItemData[]) => void;
  onRemoveItem: (itemId: string) => void;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 20px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  white-space: nowrap;
`;

const AreaTitle = styled(T.Heading6)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledPopover = styled(Popover)`
  margin-inline-end: 16px;
`;

const PopoverContent = styled(MenuItemGroup)`
  padding: 0 8px;
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  text-align: center;
  margin-block-start: 32px;
`;

const EmptyPlaceholder = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const EmptyTitle = styled(T.Heading5)`
  margin-block-start: 24px;
  max-width: 375px;
  text-align: center;
  color: ${cbSemanticCssVars.textPrimary};
`;

const EmptySubtitle = styled(T.Body2)`
  margin-block-start: 12px;
  max-width: 375px;
  text-align: center;
  color: ${cbSemanticCssVars.textSecondary};
`;

const PickingArea = ({
  items,
  isLoadingItems,
  menuId,
  draggedItemIds,
  header,
  onEdit,
  onDelete,
  onClose,
  onMoveItems,
  onRemoveItem,
}: Props) => (
  <Root>
    <Header>
      <AreaTitle>{header}</AreaTitle>
      <div>
        <StyledPopover
          showOnClick
          showOnHover={false}
          renderArrow={false}
          portalElement={null}
          contentZIndex={1}
          placement="bottom-start"
          closeOnClickInside
          content={
            <PopoverContent>
              <MenuLinkItem variant="black" onClick={onEdit}>
                <FormattedMessage id="picking-route.edit-area-name" />
              </MenuLinkItem>
              <MenuLinkItem variant="red" onClick={onDelete}>
                <FormattedMessage id="picking-route.delete-single-area" />
              </MenuLinkItem>
            </PopoverContent>
          }
        >
          <IconButton variant="blue" size="small">
            <MoreHorizontal />
          </IconButton>
        </StyledPopover>
        <IconButton variant="grey" size="small" onClick={onClose}>
          <Cross />
        </IconButton>
      </div>
    </Header>
    <Content>
      {isLoadingItems && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {!isLoadingItems && items.length === 0 && (
        <EmptyPlaceholder>
          <GirlShrugging width="343" />
          <EmptyTitle>
            <FormattedMessage id="picking-route.empty-placeholder-title" />
          </EmptyTitle>
          <EmptySubtitle>
            <FormattedMessage id="picking-route.empty-placeholder-subtitle" />
          </EmptySubtitle>
        </EmptyPlaceholder>
      )}
      {!isLoadingItems && items.length > 0 && (
        <ItemList
          canSort
          items={items}
          menuId={menuId}
          draggedItemIds={draggedItemIds}
          onMoveItems={onMoveItems}
          onRemoveItem={onRemoveItem}
        />
      )}
    </Content>
  </Root>
);

export default PickingArea;
